/**
 * HitlFileUploadPanel — file-drop HITL response form, shared between ora and
 * the Outlook plugin.
 *
 * Renders for HITL questions whose ``type === "file_upload"`` — currently the
 * carrier-mortgagee post-portal flow's "Awaiting Carrier Documents" gate. The
 * AM drops one or more files (carrier confirmation screenshots, COI, etc.);
 * on submit the panel encodes each as base64 and hands the parent-supplied
 * ``onSubmit`` a payload of the wire shape the substrate expects:
 *
 *   { instance_id, field_values: { hitl_uploaded_files: [{name, mime_type, bytes_b64}] } }
 *
 * Transport (Remix fetcher in ora, ``orchestratorApi.resumeInstance`` in the
 * plugin) stays app-specific; this component is presentational only.
 *
 * Like HitlSubmitter, the prop type for the question is intentionally loose
 * (``Record<string, unknown>``) so both ora's and the plugin's
 * ``TypedHitlQuestion`` shapes pass through without a cross-package import.
 */

import { useState } from "react";
import { FileUploadDropzone } from "~/common/components/FileUploadDropzone";

export interface HitlFileUploadPanelProps {
  /** Substrate instance id; forwarded into the submit payload. */
  instanceId: string;
  /** The HITL question payload, as forwarded from the substrate. */
  hitlQuestion: Record<string, unknown> | null | undefined;
  /**
   * Called with the resume payload. Parent is responsible for posting it to
   * the /resume endpoint (Remix fetcher in ora, orchestratorApi in plugin).
   * Returning a Promise lets us hold the "Uploading…" state until the parent
   * finishes; sync returns work too.
   */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /** Externally controlled submitting state for parents that prefer to manage it. */
  submittingOverride?: boolean;
}

/** Carrier-portal evidence: screenshots (PNG/JPG), COIs (PDF), Word notes. */
const ACCEPTED_FILE_TYPES = [".pdf", ".png", ".jpg", ".jpeg", ".docx", ".doc"];
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 10;

export function HitlFileUploadPanel({
  instanceId,
  hitlQuestion,
  onSubmit,
  submittingOverride,
}: HitlFileUploadPanelProps) {
  // Internal storage is File[] (not FileList) — FileList is immutable and only
  // constructible via the DOM DataTransfer API, which isn't available in jsdom.
  // The FileUploadDropzone only reads .length and iterates via Array.from(...),
  // so a File[] cast to FileList is structurally compatible.
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>("");
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const submitting = submittingOverride ?? internalSubmitting;

  const heading = getString(hitlQuestion, "question") ?? "Upload Documents";
  const message = getString(hitlQuestion, "message");

  const handleFileChange = (newFiles: FileList) => {
    setError("");
    const accepted: File[] = [];
    const rejectedNames: string[] = [];
    Array.from(newFiles).forEach((f) => {
      if (f.size > MAX_FILE_BYTES) {
        rejectedNames.push(f.name);
        return;
      }
      accepted.push(f);
    });
    // Keep the accepted files even when one of their siblings was rejected;
    // surfacing the rejection alone (and discarding the rest) makes the AM
    // re-drop everything, which is the bug T4+T5 reviewer flagged.
    const combined = [...files, ...accepted];
    if (combined.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} files at a time.`);
      return;
    }
    if (rejectedNames.length > 0) {
      const list = rejectedNames.map((n) => `"${n}"`).join(", ");
      setError(`${list} exceeded the 25 MB per-file limit and ${rejectedNames.length === 1 ? "was" : "were"} skipped.`);
    }
    setFiles(combined);
  };

  const handleFileRemove = (index: number) => {
    setError("");
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setError("");
    setFiles([]);
  };

  const canSubmit = files.length > 0 && !submitting && !error;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError("");
    setInternalSubmitting(true);
    try {
      const encoded = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          mime_type: file.type || "application/octet-stream",
          bytes_b64: await fileToBase64(file),
        })),
      );
      await Promise.resolve(
        onSubmit({
          instance_id: instanceId,
          field_values: { hitl_uploaded_files: encoded },
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "File upload failed.");
    } finally {
      setInternalSubmitting(false);
    }
  };

  return (
    <div
      data-testid="hitl-file-upload-panel"
      className="flex flex-col gap-3 rounded-md border border-primary-gray-light-200 bg-base-white px-4 py-3"
    >
      <h3
        className="text-base font-semibold text-primary-gray-light-800"
        data-testid="hitl-file-upload-heading"
      >
        {heading}
      </h3>
      {message && (
        <p
          className="text-sm text-primary-gray-light-700"
          data-testid="hitl-file-upload-message"
        >
          {message}
        </p>
      )}
      <FileUploadDropzone
        onFileChange={handleFileChange}
        uploadedFiles={files as unknown as FileList}
        onFileRemove={handleFileRemove}
        onClearAll={handleClearAll}
        acceptedFileTypes={ACCEPTED_FILE_TYPES}
        maxFiles={MAX_FILES}
        fileUploadMaxSize={MAX_FILE_BYTES}
        error={error}
      />
      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="hitl-file-upload-submit"
          className={
            canSubmit
              ? "px-3 py-2 text-sm font-bold rounded-lg bg-sunrise border border-sunrise text-primary-gray-light-800 hover:shadow-[0px_0px_2px_2px_rgba(255,164,0,0.24)]"
              : "px-3 py-2 text-sm font-bold rounded-lg bg-primary-gray-light-200 border border-primary-gray-light-300 text-primary-gray-light-500 cursor-not-allowed"
          }
        >
          {submitting ? "Uploading…" : "Submit Documents"}
        </button>
      </div>
    </div>
  );
}

function getString(
  obj: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  if (!obj) return undefined;
  const v = obj[key];
  return typeof v === "string" ? v : undefined;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}
